import React, { useState, useCallback } from 'react';
import { T, Var } from 'gt-react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { rpcClient } from '@/api/rpc/client';
import {
  CreateNoteInput,
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import {
  FloppyDiskIcon,
  XIcon,
  WarningCircleIcon,
  CheckCircleIcon,
  BrainIcon,
} from '@phosphor-icons/react';
import { AIWorkflow } from '@erp/shared-types';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';

interface SaveNoteToMemoryBankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note: string;
  onSaveNoteToMemoryBank: () => void;
  onSkipSaving: () => void;
  metadata?: MemoryBankNoteMetadata;
  entityId: string;
  entityType: MemoryBankSupportedEntityTypes;
}

export function SaveNoteToMemoryBankDialog({
  open,
  note,
  onSaveNoteToMemoryBank: onSave,
  onSkipSaving: onSkip,
  metadata,
  entityId,
  entityType,
}: SaveNoteToMemoryBankDialogProps) {
  const [result, setResult] = useState<{
    success: boolean;
    error?: string;
  } | null>(null);

  // Set up query invalidation
  const invalidateQueries = useInvalidateRpcQueryFn(
    (orpc) => [
      orpc.memoryBank.list.key(),
      orpc.memoryBank.search.key(),
      orpc.memoryBank.batchGet.key(),
      orpc.memoryBank.listUsages.key(),
    ],
    [],
  );

  const saveNoteMutation = useMutation({
    mutationFn: async (input: CreateNoteInput) => {
      return await rpcClient.memoryBank.create(input);
    },
    onSuccess: () => {
      setResult({ success: true });
      invalidateQueries(); // Invalidate memory bank queries
      setResult(null);
      onSave();
    },
    onError: (error) => {
      console.error('Failed to save note to Memory Bank:', error);
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const handleSave = useCallback(async () => {
    const noteMetadata = {
      workflow: AIWorkflow.EMAIL_SO_INGESTION,
      workflowStep: 'EXTRACT_SALES_ORDER' as const,
      ...(metadata?.customerEmailDomain && {
        customerEmailDomain: metadata.customerEmailDomain,
      }),
    };

    saveNoteMutation.mutate({
      note: note.trim(),
      metadata: noteMetadata,
      entityId,
      entityType,
    });
  }, [
    note,
    metadata?.customerEmailDomain,
    entityId,
    entityType,
    saveNoteMutation,
  ]);

  const handleSkip = useCallback(() => {
    setResult(null);
    onSkip();
  }, [onSkip]);

  const isProcessing = saveNoteMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={() => {}} // Prevent manual close during operation
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BrainIcon className="h-5 w-5 text-blue-600" />
            <T context="Dialog title for saving a context note to the AI Memory Bank">
              Save to Memory Bank
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="Description for save to memory bank dialog, explaining it stores context to improve AI extractions">
              Save this context note to Memory Bank to help improve future
              extractions.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 border rounded-md bg-muted/50">
            <p className="text-sm font-medium mb-2">
              <T context="Label above the context note preview in memory bank save dialog">
                Context Note:
              </T>
            </p>
            <p className="text-sm text-muted-foreground line-clamp-4">{note}</p>
          </div>

          <p className="text-sm text-muted-foreground">
            <T context="Informational text explaining how the saved note will be used by the AI for future email extractions">
              This note will be stored with metadata to help the AI make better
              extraction decisions for similar emails in the future.
            </T>
          </p>

          {/* Result Display */}
          {result && (
            <AlertContainer
              className={
                result.success
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }
            >
              <div className="flex items-center gap-2">
                {result.success ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <WarningCircleIcon className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription
                  className={result.success ? 'text-green-800' : 'text-red-800'}
                >
                  {result.success ? (
                    <T context="Success message after note was saved to Memory Bank">
                      Note saved to Memory Bank successfully!
                    </T>
                  ) : (
                    <T context="Error message when saving note to Memory Bank failed, includes the error details">
                      Failed to save note: <Var>{result.error}</Var>
                    </T>
                  )}
                </AlertDescription>
              </div>
            </AlertContainer>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isProcessing || result?.success}
          >
            <XIcon className="h-4 w-4 mr-1" />
            <T context="Button to skip saving the context note to Memory Bank">
              Skip
            </T>
          </Button>
          <Button
            onClick={handleSave}
            disabled={isProcessing || result?.success}
          >
            {isProcessing ? (
              <>
                <FloppyDiskIcon className="h-4 w-4 mr-2 animate-pulse" />
                <T context="Button label while the note is being saved to Memory Bank">
                  Saving...
                </T>
              </>
            ) : (
              <>
                <FloppyDiskIcon className="h-4 w-4 mr-1" />
                <T context="Button label to confirm saving the note to Memory Bank">
                  Save to Memory Bank
                </T>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
